import React, { useCallback, useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { Clients } from '@ariksa/notification';
import {
  Box,
  Center,
  HStack,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { filter, isEmpty, map, toLower } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import {
  PlusCountTag,
  renderRiskContext,
  renderSeverityBar,
  renderTableHeaderWithLoader,
  renderTime,
  renderTooltipFieldValue,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import {
  ChatIcon,
  GraphIcon,
  LinkIcon,
  NotifyIcon,
  PolicyExceptionIcon,
  RemediateIcon,
  SeverityIcon,
  SummaryIcon,
  TicketIcon,
} from 'components/Icons';
import { TableActionsMenu } from 'components/Overlay';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import {
  ResourceTypeIconTooltip,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { selectApp } from 'containers/App/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { securityGraphRedirectUrl } from 'containers/Visibility/SecurityGraphNext/utils/securityGraphRedirectUrl';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

export const useIndividualAlertsTableColumns = () => {
  const navigate = useNavigate();
  const snooze = useDisclosure();
  const remediate = useDisclosure();
  const exceptionModal = useDisclosure();
  const ticketModal = useDisclosure();
  const notificationModal = useDisclosure();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const { updateActiveResource } = useActiveResourceActions();
  const { onOpenMetadata } = useActiveResourceContext();
  const { getResourceAlias } = useResourceType();
  const { riskContext, alertListStatus } = useSelector(selectSharedState);
  const { renderStackedCloudAccountWithCloud } = useCloudAccountId();
  const { alertStatus } = useSearchParams();
  const location = useLocation();
  const { user } = useSelector(selectApp);
  const [copied, setCopied] = useState(false);
  const [changeSeverity, setChangeSeverity] = useState(false);
  const { environmentId } = useAccessBoundary();

  useEffect(() => {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 2000);

    // Cleanup to prevent memory leaks
    return () => clearTimeout(timer);
  }, []);

  const handleRowClick = useCallback(
    (row, tabIndex) => {
      updateActiveResource({
        tabIndex: tabIndex,
        resourceType: row?.resource,
        resourceId: row?.entity_id,
        uuid: row?.entity_uuid,
        accountId: row?.account_id,
        cloudProvider: toLower(row?.cloud) as CloudProviders,
      });
      onOpenMetadata();
    },
    [onOpenMetadata, updateActiveResource],
  );

  const renderAction = ({ row }) => {
    const tickets = filter(
      alertListStatus.data?.[row?.uuid],
      o => o.action === Clients.Jira || o.action === Clients.Servicenow,
    );
    const menuItems = [
      {
        label: 'Show security graph',
        icon: <GraphIcon />,
        onClick: () => {
          const url = securityGraphRedirectUrl({
            alert_id: row.uuid,
            resource_id: row.entity_id,
            source_resource_uuid: row.entity_uuid,
            resource_type: row.resource,
            account_id: row.account_id,
            alert_rule_id: row.alert_rule_id,
          });
          navigate(url);
        },
      },
      {
        label: 'Resource details',
        icon: <SummaryIcon />,
        onClick: () => {
          handleRowClick(row, 0);
        },
      },
      {
        label: 'Create ticket',
        icon: <TicketIcon />,
        isDisabled: !isEmpty(tickets),
        onClick: () => {
          setCurrentRecord(row);
          ticketModal.onOpen();
        },
      },
      {
        label: 'Send notification',
        icon: <NotifyIcon />,
        onClick: () => {
          setCurrentRecord(row);
          notificationModal.onOpen();
        },
      },
      {
        label: 'Add exception',
        icon: <PolicyExceptionIcon />,
        onClick: () => {
          setCurrentRecord(row);
          exceptionModal.onOpen();
        },
      },
      {
        label: 'Remediate',
        icon: <RemediateIcon />,
        onClick: () => {
          setCurrentRecord(row);
          remediate.onOpen();
        },
      },
      {
        label: 'Change severity',
        icon: <SeverityIcon />,
        onClick: () => {
          setCurrentRecord(row);
          exceptionModal.onOpen();
          setChangeSeverity(true);
        },
      },
    ];

    return (
      <Box onClick={e => e.stopPropagation()}>
        <TableActionsMenu
          menuItems={menuItems}
          styles={{
            button: {
              isDisabled: row.status === 'RESOLVED',
            },
          }}
        />
      </Box>
    );
  };

  const name = {
    header: <Box pl="thLeftPaddingWithIcon">Entity</Box>,
    accessor: 'entity_name',
    render: ({ row }) => (
      <WithResourceIcon
        resourceType={row.resource ?? ''}
        iconSize="sm"
        iconTooltip={
          <ResourceTypeIconTooltip resourceType={row.resource ?? ''} />
        }
      >
        <StackedCell
          upper={row?.entity_name ?? row?.entity_id}
          lower={getResourceAlias(row?.resource)}
          showLowerTooltip={false}
          showUpperTooltip={false}
          tooltip={
            <Stack>
              {renderTooltipFieldValue('Name', row?.entity_name)}
              {renderTooltipFieldValue('Resource ID', row?.entity_id)}
            </Stack>
          }
        />
      </WithResourceIcon>
    ),
    align: 'left',
    styles: { width: '300px', cell: { maxWidth: '300px' } },
    sortKey: 'entity_name',
  };

  const finding = {
    header: 'Description',
    render: ({ row }) => (
      <HStack w="full" justify="space-between">
        <Text whiteSpace="wrap" noOfLines={2} flex={1}>
          {row.message}
        </Text>
        <Box
          boxSize={5}
          color="primary"
          cursor="pointer"
          onClick={e => {
            e.stopPropagation();
            setCurrentRecord(row);
            navigator.clipboard.writeText(
              (
                location.origin +
                '/findings/issue/' +
                row?.uuid +
                '?environmentId=' +
                environmentId +
                '&organization=' +
                user.info.org_id
              ).toString(),
            );
            setCopied(true);
          }}
        >
          <Center>
            <CustomTooltip
              label={
                copied && currentRecord.uuid === row?.uuid
                  ? 'Link copied'
                  : 'Get link for issue'
              }
              width={20}
              textAlign="center"
            >
              <LinkIcon />
            </CustomTooltip>
          </Center>
        </Box>
      </HStack>
    ),
    align: 'left',
  };

  const account = {
    header: 'Account',
    render: ({ row }) => renderStackedCloudAccountWithCloud(row.account_id),
    align: 'left',
    styles: { width: '160px', cell: { maxWidth: '160px' } },
  };

  const context = {
    header: renderTableHeaderWithLoader('Risk Context', riskContext.isLoading),
    align: 'left',
    render: ({ row }) =>
      renderRiskContext(riskContext?.data?.[row?.entity_uuid]),
    styles: { width: '120px', cell: { maxWidth: '120px' } },
  };

  const blueprint = {
    header: 'Blueprints',
    align: 'left',
    accessor: 'blueprints',
    render: ({ value }) => {
      const renderLink = item => (
        <Box
          _hover={{ cursor: 'pointer' }}
          overflow="hidden"
          textOverflow="ellipsis"
          onClick={() => {
            navigate(`/policy-hub/blueprint/${item.blueprint_id}`);
          }}
        >
          {item?.blueprint_name}
        </Box>
      );
      return (
        <CustomTooltip
          label={
            <Stack>
              <Box borderBottom="1px solid" borderColor="orange" color="orange">
                POLICY BLUEPRINTS
              </Box>
              {map(value, o => (
                <Box>{o?.blueprint_name}</Box>
              ))}
            </Stack>
          }
        >
          <Stack spacing={0} color="primary">
            <HStack spacing={0}>
              {value[0] && renderLink(value[0])}
              <Box>{value[1] && ','}</Box>
            </HStack>
            <HStack spacing={0} align="center">
              {value[1] && renderLink(value[1])}
              {value?.length > 2 && (
                <HStack spacing={1}>
                  <Box>,</Box>
                  <PlusCountTag count={value?.length - 2} />
                </HStack>
              )}
            </HStack>
          </Stack>
        </CustomTooltip>
      );
    },
    styles: { width: '200px', cell: { maxWidth: '200px' } },
  };

  const severity = {
    header: 'Severity',
    accessor: 'severity',
    render: ({ value }) => renderSeverityBar({ value, isInline: false }),
    align: 'left',
    styles: { width: '100px', cell: { maxWidth: '100px' } },
    // Alert Vault sorts by severity rank server-side.
    ...(isAlertVaultEnabled() ? { sortKey: 'severity' } : {}),
  };

  const state = {
    header: 'State',
    accessor: 'status',
    render: ({ value, row }) =>
      row?.snooze ? (
        <HStack spacing={1}>
          <Box>Snoozed</Box>
          <Box boxSize={4} color="primary">
            <ChatIcon />
          </Box>
        </HStack>
      ) : value === 'ACTIVE' ? (
        'Open'
      ) : (
        toTitleCase(value)
      ),
    align: 'left',
    styles: { width: '100px', cell: { maxWidth: '100px' } },
  };

  const commonColumns = [name, finding, account, severity];

  const resolved = [
    ...commonColumns,
    {
      header: 'Resolved at',
      accessor: 'resolved_at',
      align: 'left',
      render: renderTime,
    },
    {
      header: 'Reason',
      accessor: 'resolved_by',
      align: 'left',
    },
  ];

  const open = [
    ...commonColumns,
    state,
    context,
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  const snoozed = [
    ...commonColumns,
    state,
    {
      header: 'Snoozed Until',
      accessor: 'unsnooze_at',
      align: 'left',
      render: renderTime,
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  const exceptions = [
    ...commonColumns,
    {
      header: 'State',
      align: 'left',
      render: () => 'Exception',
    },
    blueprint,
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  let columns: TableColumnProps[];

  switch (alertStatus) {
    case 'Resolved':
      columns = resolved;
      break;
    case 'Snoozed':
      columns = snoozed;
      break;
    case 'Exception':
      columns = exceptions;
      break;
    default:
      columns = open;
  }

  return {
    columns,
    handleRowClick,
    snooze,
    exceptionModal,
    ticketModal,
    notificationModal,
    currentRecord,
    blueprint,
    remediate,
    account,
    context,
    setCurrentRecord,
    changeSeverity,
    setChangeSeverity,
  };
};
